import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { StorageConstants } from '../constants/storage.constants';
import { ApiConstants } from '../constants/api.constants';
import { LoginRequest, LoginResponse, RefreshTokenRequest, CompanyInfo } from './auth.model';
import { User } from '../models/user.model';
import { ApiResponse } from '../models/api-response.model';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private currentUserSubject = new BehaviorSubject<User | null>(this.getStoredUser());
  public currentUser$ = this.currentUserSubject.asObservable();

  // ── Active company context ────────────────────────────────────────────────
  // Stores the full CompanyInfo of the currently selected company.
  // Persisted to localStorage as JSON so it survives page refresh.
  // The AuthInterceptor reads getSelectedCompany().compCode and sends it as
  // the X-Company-Code request header on every API call.

  private selectedCompanySubject = new BehaviorSubject<CompanyInfo | null>(
    this.getStoredSelectedCompany()
  );
  public selectedCompany$ = this.selectedCompanySubject.asObservable();

  /**
   * True while a logout / session-clear is in progress. Screens that react to
   * selectedCompany$ can check this to skip the "No active company selected"
   * warning during teardown.
   */
  public isLoggingOut = false;

  constructor(private http: HttpClient, private router: Router) {}

  // ─── Login ────────────────────────────────────────────────────────────────

  login(request: LoginRequest): Observable<ApiResponse<LoginResponse>> {
    return this.http.post<ApiResponse<LoginResponse>>(
      `${environment.apiUrl}${ApiConstants.AUTH_LOGIN}`, request
    ).pipe(
      tap(response => {
        if (response.success && response.data) {
          this.storeSession(response.data);
        }
      })
    );
  }

  // ─── Refresh Token ────────────────────────────────────────────────────────

  refreshAccessToken(): Observable<ApiResponse<LoginResponse>> {
    const refreshToken = localStorage.getItem(StorageConstants.REFRESH_TOKEN_KEY) ?? '';
    const body: RefreshTokenRequest = { refreshToken };

    return this.http.post<ApiResponse<LoginResponse>>(
      `${environment.apiUrl}${ApiConstants.AUTH_REFRESH}`, body
    ).pipe(
      tap(response => {
        if (response.success && response.data) {
          this.storeSession(response.data);
        }
      })
    );
  }

  // ─── Logout ───────────────────────────────────────────────────────────────

  logout(): void {
    const refreshToken = localStorage.getItem(StorageConstants.REFRESH_TOKEN_KEY);
    this.http
      .post<ApiResponse<boolean>>(
        `${environment.apiUrl}${ApiConstants.AUTH_LOGOUT}`,
        { refreshToken }
      )
      .subscribe({
        next:  () => this.clearSessionAndRedirect(),
        error: () => this.clearSessionAndRedirect(),
      });
  }

  // ─── Company context ──────────────────────────────────────────────────────

  /** Returns the full CompanyInfo of the currently selected company, or null. */
  getSelectedCompany(): CompanyInfo | null {
    return this.selectedCompanySubject.value;
  }

  /**
   * Sets the active company, persists it to localStorage, and emits to
   * all subscribers (header display, AuthInterceptor, etc.).
   */
  setSelectedCompany(company: CompanyInfo): void {
    localStorage.setItem(StorageConstants.COMPANY_CONTEXT_KEY, JSON.stringify(company));
    this.selectedCompanySubject.next(company);
  }

  /**
   * Synchronises the shared header company list with a freshly-fetched list
   * (e.g. after a company is deleted on the admin grid).
   *
   * This updates the in-memory User.companies and its cached copy in
   * localStorage so the header selector reflects the change immediately —
   * without a re-login, a page reload, or a second GetList call.
   *
   * The supplied list is reconciled against the user's currently accessible
   * companies: only companies the user already had access to are kept, so a
   * deleted company simply disappears from the header.
   *
   * If the currently selected company is no longer present, the active
   * company context (and its cache) is cleared. No company is auto-selected
   * unless such a rule already existed elsewhere in the app.
   */
  syncCompanyList(companies: CompanyInfo[]): void {
    const user = this.currentUserSubject.value;
    if (!user) return;

    const existing = user.companies ?? [];
    const existingCodes = new Set(existing.map(c => c.compCode));

    // Keep only companies the user already had access to, refreshing their
    // details from the incoming list. This removes deleted companies while
    // never granting access to new ones the user shouldn't see.
    const incomingByCode = new Map(companies.map(c => [c.compCode, c]));
    const reconciled = existing
      .filter(c => incomingByCode.has(c.compCode))
      .map(c => incomingByCode.get(c.compCode)!);

    // Nothing changed — skip the write and the emit.
    const unchanged =
      reconciled.length === existing.length &&
      reconciled.every(c => existingCodes.has(c.compCode));
    if (unchanged) return;

    const updatedUser: User = { ...user, companies: reconciled };
    localStorage.setItem(StorageConstants.USER_KEY, JSON.stringify(updatedUser));
    this.currentUserSubject.next(updatedUser);

    // If the active company was removed, resolve the selection:
    //   - exactly one company left  -> auto-select it (no ambiguity),
    //   - otherwise                 -> clear the selection and its cache.
    const selected = this.selectedCompanySubject.value;
    if (selected && !reconciled.some(c => c.compCode === selected.compCode)) {
      this.resolveSelectionAfterRemoval(reconciled);
    }
  }

  /**
   * Refreshes the shared header company list from a freshly-fetched GetList
   * response, treating that response as the authoritative set of companies the
   * user can access (e.g. after a company is created or edited on the admin
   * grid).
   *
   * Unlike syncCompanyList (removal-only, used by delete), this method also
   * ADDS newly accessible companies — the GetList endpoint is already scoped
   * server-side to the caller, so any company it returns is one the user is
   * allowed to see. Persists to localStorage and emits, so the header updates
   * immediately without a re-login, page reload, or second GetList call.
   */
  refreshCompanyList(companies: CompanyInfo[]): void {
    const user = this.currentUserSubject.value;
    if (!user) return;

    const existing = user.companies ?? [];
    const next = companies.map(c => ({
      compCode: c.compCode,
      companyName: c.companyName,
      shortName: c.shortName,
    }));

    // Nothing changed (same codes, same order) — skip the write and the emit.
    const unchanged =
      next.length === existing.length &&
      next.every((c, i) => c.compCode === existing[i]?.compCode);
    if (unchanged) return;

    const updatedUser: User = { ...user, companies: next };
    localStorage.setItem(StorageConstants.USER_KEY, JSON.stringify(updatedUser));
    this.currentUserSubject.next(updatedUser);

    // If the active company is no longer present (e.g. it was removed),
    // resolve the selection the same way as after a delete.
    const selected = this.selectedCompanySubject.value;
    if (selected && !next.some(c => c.compCode === selected.compCode)) {
      this.resolveSelectionAfterRemoval(next);
    } else if (!selected && next.length > 0) {
      // No company was selected before this call (zero-company → first-company
      // scenario). Auto-select the first company in the new list so the header
      // badge/dropdown appears immediately — mirrors the storeSession logic.
      this.setSelectedCompany(next[0]);
    }
  }

  /**
   * Resolves the active-company selection after the previously selected
   * company was removed from the accessible list.
   *
   * Rule: fall back to the TOP company remaining in the list (index 0) so
   * every screen keeps a valid company context to load. The company code is
   * taken dynamically from the list — nothing is hardcoded. Only when no
   * companies remain is the selection (and its cache) cleared.
   */
  private resolveSelectionAfterRemoval(remaining: CompanyInfo[]): void {
    if (remaining.length > 0) {
      this.setSelectedCompany(remaining[0]);
    } else {
      localStorage.removeItem(StorageConstants.COMPANY_CONTEXT_KEY);
      this.selectedCompanySubject.next(null);
    }
  }

  // ─── Helpers ─────────────────────────────────────────────────────────────

  getToken(): string | null {
    return localStorage.getItem(StorageConstants.TOKEN_KEY);
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(StorageConstants.REFRESH_TOKEN_KEY);
  }

  isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) return false;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp * 1000 > Date.now();
    } catch {
      return false;
    }
  }

  getCurrentUser(): User | null {
    return this.currentUserSubject.value;
  }

  /** Returns the roleCode of the current user, or null if not logged in. */
  getRole(): string | null {
    return this.currentUserSubject.value?.roleCode ?? null;
  }

  /** Returns true only when the logged-in user has the SYSADMIN role. */
  isSysAdmin(): boolean {
    return this.getRole() === 'SYSADMIN';
  }

  /** Returns true when the logged-in user has the ADMINISTRATOR role. */
  isAdministrator(): boolean {
    return this.getRole() === 'ADMINISTRATOR';
  }

  /** Returns true when the user is SYSADMIN or ADMINISTRATOR. */
  isPrivilegedUser(): boolean {
    return this.isSysAdmin() || this.isAdministrator();
  }

  clearSessionAndRedirect(reason?: string): void {
    // Flag the teardown so screens subscribed to selectedCompany$ don't show a
    // "No active company selected" warning while we clear the session.
    this.isLoggingOut = true;

    localStorage.removeItem(StorageConstants.TOKEN_KEY);
    localStorage.removeItem(StorageConstants.REFRESH_TOKEN_KEY);
    localStorage.removeItem(StorageConstants.USER_KEY);
    localStorage.removeItem(StorageConstants.REMEMBER_ME_KEY);
    localStorage.removeItem(StorageConstants.COMPANY_CONTEXT_KEY);
    // NOTE: the device id is intentionally preserved so the same browser keeps
    // a stable audit identity across logins.
    sessionStorage.clear();
    this.currentUserSubject.next(null);

    // Navigate to /login FIRST so the current feature screen is destroyed and
    // its selectedCompany$ subscription (takeUntil(destroy$)) is torn down
    // before we emit the cleared company — otherwise clearing the company would
    // trigger that screen's "No active company selected" warning on logout.
    this.router
      .navigate(['/login'], reason ? { queryParams: { reason } } : undefined)
      .finally(() => {
        this.selectedCompanySubject.next(null);
        this.isLoggingOut = false;
      });
  }

  private storeSession(data: LoginResponse): void {
    localStorage.setItem(StorageConstants.TOKEN_KEY, data.accessToken);
    localStorage.setItem(StorageConstants.REFRESH_TOKEN_KEY, data.refreshToken);

    const user: User = {
      userId:    0,
      username:  data.username,
      email:     '',
      roleCode:  data.roleCode,
      roleName:  data.roleName,
      userType:  'A',
      // Ensure companies is always CompanyInfo[] even if the server returns
      // plain strings (backwards compatibility during migration).
      companies: (data.companies ?? []).map((c: any) =>
        typeof c === 'string'
          ? { compCode: c, companyName: c, shortName: c }
          : c
      ) as CompanyInfo[],
    };
    localStorage.setItem(StorageConstants.USER_KEY, JSON.stringify(user));
    this.currentUserSubject.next(user);

    // Auto-select company on login / token refresh:
    //   - If a company is already stored AND it still exists in the new list → keep it.
    //   - Otherwise pick the first company in the list.
    const existing   = this.getStoredSelectedCompany();
    const companies  = data.companies ?? [];
    const stillValid = existing && companies.some(c => c.compCode === existing.compCode);

    if (stillValid) {
      // Re-emit the persisted company so subscribers are notified after login
      this.selectedCompanySubject.next(existing!);
    } else if (companies.length > 0) {
      this.setSelectedCompany(companies[0]);
    }
  }

  private getStoredUser(): User | null {
    const json = localStorage.getItem(StorageConstants.USER_KEY);
    if (!json) return null;
    try {
      const user = JSON.parse(json) as User;
      // Guard against old format where companies were plain strings.
      // Migrate to empty array so the app doesn't crash — user will re-login.
      if (user.companies?.length > 0 && typeof user.companies[0] === 'string') {
        user.companies = [];
        localStorage.removeItem(StorageConstants.COMPANY_CONTEXT_KEY);
      }
      return user;
    } catch {
      return null;
    }
  }

  private getStoredSelectedCompany(): CompanyInfo | null {
    const json = localStorage.getItem(StorageConstants.COMPANY_CONTEXT_KEY);
    if (!json) return null;
    try {
      const parsed = JSON.parse(json);
      // Guard against old format where companies were stored as plain strings.
      // If the stored value has no compCode property it is stale — discard it.
      if (typeof parsed === 'string' || !parsed?.compCode) {
        localStorage.removeItem(StorageConstants.COMPANY_CONTEXT_KEY);
        return null;
      }
      return parsed as CompanyInfo;
    } catch {
      localStorage.removeItem(StorageConstants.COMPANY_CONTEXT_KEY);
      return null;
    }
  }
}
